import React, { useEffect, useReducer, useState } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import { selectIsLoggedIn } from "../../redux/authSlice";
import MutipleChoiceQuestion from "../MultipleChoiceQuestion";
import HighChart from "../HighChart";
import { formatPercent } from "../../helpers/utils";
import ProgressDots from "../ProgressDots/ProgressDots";
import _ from 'lodash';
import styles from './BenchmarkingWizardDialog.module.css';
import { Snackbar, Alert } from "@mui/material";

const getAnswerIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#aaa">
            <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h357l-80 80H200v560h560v-278l80-80v358q0 33-23.5 56.5T760-120H200Zm280-360ZM360-360v-170l367-367q12-12 27-18t30-6q16 0 30.5 6t26.5 18l56 57q11 12 17 26.5t6 29.5q0 15-5.5 29.5T897-728L530-360H360Zm481-424-56-56 56 56ZM440-440h56l232-232-28-28-29-28-231 231v57Zm260-260-29-28 29 28 28 28-28-28Z" />
        </svg>
    )
}

function getRandomQuestions(questions) {
	// If array has 3 or fewer elements, return it as is
	if (questions.length <= 3) {
		return questions;
	}

	const selectedQuestions = [];
	for (let i = 0; i < 3; i++) {
		const randomIndex = Math.floor(Math.random() * questions.length);
		selectedQuestions.push(questions[randomIndex]);
		questions.splice(randomIndex, 1);
	}

	return selectedQuestions;
}

function getChartData(question) {

	// This function is a temporary hack

	// The response for the call to /surveyQuestions/[surveyQuestionId]/results
	// is different than the response for the call to /benchmarking/wizard

	// So, the chartData parameter object  we typically pass to the <HighChart> component
	// is different than the question parameter object we pass to the <MultipleChoiceQuestion> component

	// So, this function will generate a chartData object from a question object

	// Ideally, this should be refactored so that both the <HighChart> and <MultipleChoiceQuestion>
	// take a consistently structured parameter

	const chartData = {
		surveyQuestionId: question.surveyQuestionId,
		questionId: question.questionId,
		type: question.surveyQuestionType,
		question: question.shortText,
		fullQuestion: question.text,
		chartType: question.chartType,
		peersOnly: question.peersOnly,
		respondents: question.respondents,
		responses: _.cloneDeep(question.chartData.responses)
	}
	return chartData;
}

const reducer = (state, action) => {

	switch (action.type) {
		case ACTION.NEW_QUESTIONS:
			const questionPool = [];
			const showInsightsTour = action.payload.showInsightsTour;
			const showBenchmarkingTour = action.payload.showBenchmarkingTour;
			let benchmarkingComplete = true;
			for (let question of action.payload.questions) {
				if ((question.responses === undefined) || (question.responses.length === 0)) {
					benchmarkingComplete = false;
					questionPool.push(_.cloneDeep(question));
				}
			}
			return { ...state, questions: getRandomQuestions(questionPool), dataLoaded: true, benchmarkingComplete, showInsightsTour, showBenchmarkingTour };
		case ACTION.NEW_RESPONSES:
			const surveyQuestionId = action.payload.surveyQuestionId;
			const newQuestions = _.cloneDeep(state.questions);
			for (let question of newQuestions) {
				if (question.surveyQuestionId === surveyQuestionId) {
					question.responses = [...action.payload.responses];
				}
			}
			return { ...state, questions: newQuestions };
		default:
			console.log(`unhandled action.type: ${action.type}`)
	}
}

const ACTION = {
	NEW_QUESTIONS: "newQuestions",
	NEW_RESPONSES: "newResponses",
}
function NumberedQuestions({ questions, containerStyles, itemStyles }) {
	if (!questions) {
		return null
	}
	return (
		<div className={styles.wizardQuestionsContainer} style={containerStyles}>
			{
				questions.map((question, i) => {
					return (
						<div key={i} className={styles.wizardQuestionContainer} style={itemStyles}>
							<ol className="title">
								<li value={i + 1}>{question.text}</li>
							</ol>
						</div>
					)
				})
			}
		</div>
	)
}
function QuestionsAndResults({ questions, containerStyles, itemStyles, saveResponse }) {
	if (!questions) {
		return null
	}
	return (
		<div className={styles.wizardQuestionsAndResultsContainer} style={containerStyles}>
			{
				questions.map((question, i) => {
					const chartData = getChartData(question);
					return (
						<React.Fragment key={`question${i}`}>
							<div className={styles.wizardQuestionOrResultsContainer} style={itemStyles}>
								<MutipleChoiceQuestion question={question} answerChoices={question.answerChoices} responses={question.responses} saveResponse={saveResponse} compact={true} />
							</div>
							<div className={styles.wizardQuestionOrResultsContainer} style={itemStyles}>
								<HighChart chartData={chartData} chartHeight={400} />
							</div>
						</React.Fragment>
					)
				})
			}
		</div>
	)
}


const BenchmarkingWizardDialog = ({ onClose, onResponse }) => {

	// console.log(`BenchmarkingWizardDialog: render at ${new Date().getTime()}`);

	const axiosPrivate = useAxiosPrivate();

	const isLoggedIn = useSelector(selectIsLoggedIn);

	const [state, dispatch] = useReducer(reducer, {
		showInsightsTour: false,
		showBenchmarkingTour: false,
		questions: [],
		benchmarkingComplete: false,
		dataLoaded: false
	});

	const location = useLocation();
	const path = location.pathname;

	const [index, setIndex] = useState(0);

	const [showWizard, setShowWizard] = useState(false);

	const [alertStatus, setAlertStatus] = useState({
		open: false,
		severity: null,
		message: null,
	});	

	useEffect(() => {
		let
			show;

		show = false;

		if (path) {
			if (state.dataLoaded) {
				if (!state.benchmarkingComplete) {
					if (((path === '/') || (path === '/benchmarking') || (path === '/benchmarking/')) && !state.showBenchmarkingTour) {
						show = true;
					} else if ((path === '/insights' || path === '/insights/') && !state.showInsightsTour) {
						show = true;
					}
				}
			}
		}
		setShowWizard(show);
	}, [path, state.dataLoaded, state.benchmarkingComplete, state.showInsightsTour, state.showBenchmarkingTour]);

	useEffect(() => {

		if (!isLoggedIn) {
			return;
		}

		let isMounted = true;
		const controller = new AbortController();

		const getData = async () => {
			// console.log(`BenchmarkingWizardDialog: getData at ${new Date().getTime()}`);
			try {
				const response = await axiosPrivate.get('/benchmarking/wizard', {
					signal: controller.signal
				});
				if (isMounted) {
					dispatch({
						type: ACTION.NEW_QUESTIONS, payload: response.data
					});
				}
			} catch (err) {
				if (isMounted) {
					console.error(err);
				}
			}
		}

		getData();

		return () => {
			isMounted = false;
			controller.abort();
		}
	}, [isLoggedIn]) // eslint-disable-line react-hooks/exhaustive-deps

	const saveResponse = async (params) => {

		const json = { ...params };

		try {
			const response = await axiosPrivate.post(`/surveyQuestions/${params.surveyQuestionId}`,
				JSON.stringify(json),
				{
					headers: { 'Content-Type': 'application/json' }
				}
			);
			onResponse(params.surveyQuestionId)
			dispatch({
				type: ACTION.NEW_RESPONSES, payload: { ...response.data }
			});
            setAlertStatus(prevState => ({
                ...prevState,
                severity: "success",
                message: "Response saved",
                open: true
            }));			
		} catch (err) {
			console.log(err);
		}
	}

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertStatus(prevState => ({
            ...prevState,
            open: false
        }));
    };
		
	const questions = state.questions.length;
	const screens = 2;
	const steps = questions * 2 + 1;
	const dialogWidth = 800;
	const dialogWidthPx = `${dialogWidth}px`;

	const screenIndex = (index === (steps - 1) ? 1 : 0);
	const questionIndex = Math.min(Math.floor(index / 2), questions - 1);
	const questionOrResultsIndex = Math.min(index, questions * 2 - 1);
	// console.log(`index: ${index} screenIndex: ${screenIndex} questionIndex: ${questionIndex} questionOrResultsIndex: ${questionOrResultsIndex}`)

	const dialogContainerStyles = {
		width: dialogWidthPx,
	}

	const screensContainerStyles = {
		width: `${(screens * 100)}%`,
		transform: `translateX(${formatPercent(-100 * screenIndex / 2, 2)}`
	}
	const screenContainerStyles = {
		width: dialogWidthPx
	}

	const questionsContainerStyles = {
		width: `${(questions * 100)}%`,
		transform: `translateX(${formatPercent(-100 * questionIndex / questions, 2)}`
	}
	const questionContainerStyles = {
		width: dialogWidthPx
	}

	const questionAndResultsStyles = {
		width: `${(questions * 2 * 100)}%`,
		transform: `translateX(${formatPercent(-100 * questionOrResultsIndex / (questions * 2), 2)}`
	}
	const questionOrResultsStyles = {
		width: dialogWidthPx
	}

	const showPrevQuestion = () => {
		setIndex((prev) => Math.max(0, prev - 1));
	}
	const showNextQuestion = () => {
		setIndex((prev) => Math.min(steps, prev + 1));
	}

	return (
		showWizard &&
		<div className="modal dark">
			<div className={styles.wizardDialog} style={dialogContainerStyles}>
				<h2>Not sure where to begin?</h2>
				<div className="wizard-screens-container" style={screensContainerStyles}>
					<div className="wizard-screen-container" style={screenContainerStyles}>
						<p style={{ padding: "0 2rem" }}>To help you get started, we've selected a few sample questions from our benchmarking surveys.</p>
						<NumberedQuestions questions={state.questions} containerStyles={questionsContainerStyles} itemStyles={questionContainerStyles} />
						<QuestionsAndResults questions={state.questions} containerStyles={questionAndResultsStyles} itemStyles={questionOrResultsStyles} saveResponse={saveResponse} />
					</div>
					<div className="wizard-screen-container" style={screenContainerStyles}>
						<div className={styles.lastScreen}>
							<p>To learn more about how your practice compares to other advisors', click <span className={styles.linkcolor}>Begin Survey</span> for any of the Benchmarking topics in this section, or click any of the {getAnswerIcon()} icons to respond to a specific topic.</p>
							<button className="arrowed" onClick={onClose}>Explore Benchmarking</button>
						</div>
					</div>
				</div>
				<div className={styles.toolbar}>
					<button className="link" onClick={showPrevQuestion} disabled={index <= 0}>&lt; Back</button>
					<ProgressDots totalSteps={steps} currentStep={index + 1} />
					<button className="link" onClick={showNextQuestion} disabled={index >= (steps - 1)}>Next &gt;</button>
				</div>
				<button className="close" onClick={onClose}></button>
				<Snackbar open={alertStatus.open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
					<Alert onClose={handleClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>{alertStatus.message}</Alert>
				</Snackbar>
			</div >
		</div>
	)
}

export default BenchmarkingWizardDialog